import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { authApiCall, syncFromSheets } from '../../services/api';
import FormVendorPanel from './FormVendorPanel';
import { usePermission } from '../../contexts/PermissionContext';

// ===== URL 解析工具函數 =====

/**
 * 解析 Google Sheet URL，自動取得 Sheet ID
 */
const parseGoogleSheetUrl = (url) => {
  if (!url) return '';
  const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  return match ? match[1] : url;
};

/**
 * 解析 Google Form 預填連結，自動取得基礎 URL 和所有 Entry ID
 */
const parsePrefilledFormUrl = (url) => {
  if (!url) return { baseUrl: '', entries: [] };
  const baseUrl = url.split('?')[0];
  const entryRegex = /entry\.(\d+)=([^&]*)/g;
  const entries = [];
  let match;
  while ((match = entryRegex.exec(url)) !== null) {
    entries.push({ id: match[1], value: decodeURIComponent(match[2]) });
  }
  return { baseUrl, entries };
};

/**
 * 從預填連結的 entries 中智能識別供應商ID、驗證碼、供應商名稱的 Entry ID。
 * 注意：這只是「自動填入輔助」，最終仍以表單上三個可編輯欄位為準（不靠猜也能手動修正）。
 */
const identifyEntryIds = (entries) => {
  let vendorIdEntryId = '';
  let tokenEntryId = '';
  let vendorNameEntryId = '';

  for (const entry of entries) {
    const valueLower = entry.value.toLowerCase();
    // 先判斷「名稱」，避免「供應商名稱」被供應商ID規則先吃掉
    if (valueLower.includes('名稱') || valueLower.includes('name')) {
      vendorNameEntryId = entry.id;
    } else if (valueLower.includes('token') || valueLower.includes('驗證') ||
               valueLower.includes('code') || valueLower.includes('碼')) {
      tokenEntryId = entry.id;
    } else if (valueLower.includes('vendor') || valueLower.includes('供應商') ||
        valueLower.includes('id') || valueLower.includes('統編')) {
      vendorIdEntryId = entry.id;
    }
  }

  // 關鍵字辨識不到時，依出現順序遞補（ID → 驗證碼 → 名稱）
  if (entries.length >= 3) {
    if (!vendorIdEntryId) vendorIdEntryId = entries[0].id;
    if (!tokenEntryId) tokenEntryId = entries[1].id;
    if (!vendorNameEntryId) vendorNameEntryId = entries[2].id;
  } else if (entries.length === 2) {
    if (!vendorIdEntryId) vendorIdEntryId = entries[0].id;
    if (!tokenEntryId) tokenEntryId = entries[1].id;
  } else if (entries.length === 1 && !vendorIdEntryId) {
    vendorIdEntryId = entries[0].id;
  }

  return { vendorIdEntryId, tokenEntryId, vendorNameEntryId };
};

// ===== API 函數 =====
const formsAPI = {
  getAll: (projectId) => authApiCall(`/api/projects/${projectId}/forms`),
  create: (projectId, data) => authApiCall(`/api/projects/${projectId}/forms`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (projectId, formId, data) => authApiCall(`/api/projects/${projectId}/forms/${formId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  delete: (projectId, formId) => authApiCall(`/api/projects/${projectId}/forms/${formId}`, {
    method: 'DELETE',
  }),
};

// API base URL
const getApiBaseUrl = () => {
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:5001/bmc-project-2025/us-central1/api';
  }
  return 'https://us-central1-bmc-project-2025.cloudfunctions.net/api';
};

/**
 * 專案問卷管理 Tab（完整版）
 */
export default function ProjectFormsTab({ projectId }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [expandedFormId, setExpandedFormId] = useState(null);
  const [syncingFormId, setSyncingFormId] = useState(null);
  const [redeployingFormId, setRedeployingFormId] = useState(null);
  const queryClient = useQueryClient();

  // 取得問卷列表
  const { data, isLoading, error } = useQuery({
    queryKey: ['projects', projectId, 'forms'],
    queryFn: () => formsAPI.getAll(projectId),
    staleTime: 2 * 60 * 1000,
  });

  const forms = data?.forms || [];

  // 將後端錯誤轉成適當的 toast；網域違規顯示較寬、有層次的版面
  const showApiError = (prefix, err) => {
    const isDomainGuard =
      err?.code === 'DOMAIN_NOT_ALLOWED' ||
      err?.code === 'OWNER_LOOKUP_FAILED' ||
      err?.code === 'SERVICE_ACCOUNT_NO_ACCESS';

    if (!isDomainGuard) {
      toast.error(`${prefix}：${err.message}`);
      return;
    }

    const titleByCode = {
      DOMAIN_NOT_ALLOWED: '帳號不符合管制規則',
      OWNER_LOOKUP_FAILED: '無法驗證檔案擁有者',
      SERVICE_ACCOUNT_NO_ACCESS: '服務帳戶權限不足',
    };
    const title = titleByCode[err?.code] || '帳號不符合管制規則';

    toast.custom(
      (t) => (
        <div
          className={`bg-white border border-red-200 shadow-xl rounded-lg flex gap-3 p-4 ${
            t.visible ? 'animate-enter' : 'animate-leave'
          }`}
          style={{ maxWidth: 520 }}
        >
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-lg">
            ⚠
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-red-700 mb-1">
              {prefix}：{title}
            </div>
            <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line break-words">
              {err.message}
            </div>
          </div>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 text-lg leading-none"
            aria-label="關閉"
          >
            ×
          </button>
        </div>
      ),
      { duration: 15000 }
    );
  };

  // 建立問卷
  const createMutation = useMutation({
    mutationFn: (formData) => formsAPI.create(projectId, formData),
    onSuccess: () => {
      toast.success('問卷已建立');
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'forms'] });
      setShowAddModal(false);
    },
    onError: (err) => showApiError('建立失敗', err),
  });

  // 更新問卷
  const updateMutation = useMutation({
    mutationFn: ({ formId, data }) => formsAPI.update(projectId, formId, data),
    onSuccess: () => {
      toast.success('問卷已更新');
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'forms'] });
      setShowEditModal(false);
      setSelectedForm(null);
    },
    onError: (err) => showApiError('更新失敗', err),
  });

  // 刪除問卷
  const deleteMutation = useMutation({
    mutationFn: (formId) => formsAPI.delete(projectId, formId),
    onSuccess: () => {
      toast.success('問卷已刪除');
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'forms'] });
    },
    onError: (err) => toast.error(`刪除失敗: ${err.message}`),
  });

  // 同步 Google Sheets
  const handleSync = async (formId, formName) => {
    if (!confirm(`確定要從 Google Sheets 同步「${formName}」的資料嗎？`)) return;
    try {
      setSyncingFormId(formId);
      const result = await syncFromSheets(formId, projectId);
      toast.success(result.message || '同步成功！');
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'forms'] });
    } catch (error) {
      toast.error('同步失敗：' + (error.message || '未知錯誤'));
    } finally {
      setSyncingFormId(null);
    }
  };

  // 重新部署 Apps Script
  const handleRedeployScript = async (formId, formName) => {
    if (!confirm(`確定要重新部署「${formName}」的 Apps Script 嗎？`)) return;
    try {
      setRedeployingFormId(formId);
      const result = await authApiCall(`/api/projects/${projectId}/forms/${formId}/redeploy-script`, {
        method: 'POST',
      });
      toast.success(result.message || 'Apps Script 重新部署成功！');
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'forms'] });
    } catch (error) {
      showApiError('重新部署失敗', error);
    } finally {
      setRedeployingFormId(null);
    }
  };

  const handleDelete = (formId, formName) => {
    if (confirm(`確定要刪除「${formName}」嗎？此操作無法復原。`)) {
      deleteMutation.mutate(formId);
    }
  };

  const handleEdit = (form) => {
    setSelectedForm(form);
    setShowEditModal(true);
  };

  const toggleExpand = (formId) => {
    setExpandedFormId(expandedFormId === formId ? null : formId);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('zh-TW');
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">載入失敗: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 標題與操作 */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">問卷管理</h3>
          <p className="text-sm text-gray-500 mt-1">管理專案的 ESG 問卷和供應商指派</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2"
        >
          <span>+</span>
          新增問卷
        </button>
      </div>

      {/* 問卷列表 */}
      {forms.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-5xl mb-4">📝</div>
          <p className="text-gray-500 mb-4">尚無問卷</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
          >
            新增第一份問卷
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {forms.map((form) => (
            <FormCard
              key={form.id}
              form={form}
              projectId={projectId}
              isExpanded={expandedFormId === form.id}
              onToggleExpand={() => toggleExpand(form.id)}
              onEdit={() => handleEdit(form)}
              onSync={() => handleSync(form.id, form.name)}
              isSyncing={syncingFormId === form.id}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}

      {/* 使用說明 */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">使用流程</h4>
        <ol className="list-decimal list-inside space-y-1 text-xs text-blue-800">
          <li>點擊「新增問卷」建立新的 ESG 問卷</li>
          <li>貼上 Google Sheet 網址（系統自動解析 Sheet ID）</li>
          <li>貼上 Google Form 預填連結（系統自動解析 Entry ID）</li>
          <li>系統會自動部署 Apps Script 實現自動同步</li>
        </ol>
      </div>

      {/* 新增問卷 Modal */}
      {showAddModal && (
        <FormModal
          mode="add"
          onClose={() => setShowAddModal(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
        />
      )}

      {/* 編輯問卷 Modal */}
      {showEditModal && selectedForm && (
        <FormModal
          mode="edit"
          initialData={selectedForm}
          onClose={() => { setShowEditModal(false); setSelectedForm(null); }}
          onSubmit={(data) => updateMutation.mutate({ formId: selectedForm.id, data })}
          isLoading={updateMutation.isPending}
          onDelete={() => {
            if (confirm(`確定要刪除「${selectedForm.name}」嗎？此操作無法復原。`)) {
              deleteMutation.mutate(selectedForm.id);
              setShowEditModal(false);
              setSelectedForm(null);
            }
          }}
          onRedeploy={() => handleRedeployScript(selectedForm.id, selectedForm.name)}
          onSync={() => handleSync(selectedForm.id, selectedForm.name)}
          isDeleting={deleteMutation.isPending}
          isRedeploying={redeployingFormId === selectedForm.id}
          isSyncing={syncingFormId === selectedForm.id}
        />
      )}
    </div>
  );
}

/**
 * 問卷卡片組件（可展開）
 */
function FormCard({
  form,
  projectId,
  isExpanded,
  onToggleExpand,
  onEdit,
  onSync,
  isSyncing,
  formatDate,
}) {
  const hasSheetId = form.google_sheet_id || form.sheet_id;
  const hasAppsScript = form.apps_script?.script_id;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition">
      {/* 問卷標題列 */}
      <div className="p-4 flex items-start justify-between">
        <div className="flex-1 cursor-pointer" onClick={onToggleExpand}>
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-gray-900">{form.name}</h4>
            {/* 狀態標籤 */}
            {hasAppsScript ? (
              <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">已部署</span>
            ) : hasSheetId ? (
              <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full">未部署</span>
            ) : (
              <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded-full">未設定</span>
            )}
            <span className="text-gray-400">{isExpanded ? '▲' : '▼'}</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">{form.description || '無描述'}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            <span>建立於 {formatDate(form.created_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasSheetId && (
            <button
              onClick={onSync}
              disabled={isSyncing}
              className="px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition disabled:opacity-50"
            >
              {isSyncing ? '同步中...' : '🔄 同步'}
            </button>
          )}
          <button
            onClick={onEdit}
            className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition"
          >
            ⚙️ 設定
          </button>
        </div>
      </div>

      {/* 展開區域 - 只顯示供應商指派 */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <FormVendorPanel
            projectId={projectId}
            formId={form.id}
            formName={form.name}
          />
        </div>
      )}
    </div>
  );
}

/**
 * 問卷設定 Modal（三分頁設計）
 * Tab 1: 基本資訊 - 問卷名稱、描述、Google Sheet/Form 設定
 * Tab 2: 連結測試 - 連結驗證、測試按鈕
 * Tab 3: 進階設定 - Apps Script 授權/部署、刪除問卷
 */
function FormModal({ mode, initialData, onClose, onSubmit, isLoading, onDelete, onRedeploy, onSync, isDeleting, isRedeploying, isSyncing }) {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    sheetUrl: initialData?.google_sheet_id ? `https://docs.google.com/spreadsheets/d/${initialData.google_sheet_id}/edit` : '',
    sheetId: initialData?.google_sheet_id || initialData?.sheet_id || '',
    sheetName: initialData?.sheet_name || '表單回覆 1',
    prefilledUrl: initialData?.google_form_prefilled_url || initialData?.google_form_url || '',
    formUrl: initialData?.google_form_url || '',
    vendorIdEntryId: initialData?.vendor_id_entry_id || '',
    tokenEntryId: initialData?.token_entry_id || '',
    vendorNameEntryId: initialData?.vendor_name_entry_id || '',
  });

  const { hasWildcardAccess } = usePermission();
  const isAdmin = hasWildcardAccess();

  // Apps Script 授權狀態
  const [authStatus, setAuthStatus] = useState({ loading: true, authorized: false });

  // 檢查授權狀態
  const checkAuthStatus = async () => {
    try {
      setAuthStatus({ loading: true, authorized: false });
      const response = await fetch(`${getApiBaseUrl()}/auth/status`);
      const data = await response.json();
      setAuthStatus({ loading: false, authorized: data.authorized });
    } catch (error) {
      console.error('檢查授權狀態失敗:', error);
      setAuthStatus({ loading: false, authorized: false });
    }
  };

  // 初始化時檢查授權狀態（僅編輯模式）
  React.useEffect(() => {
    if (mode === 'edit') {
      checkAuthStatus();
    }
  }, [mode]);

  // 重新授權
  const handleReauthorize = () => {
    window.open(`${getApiBaseUrl()}/auth/authorize`, '_blank', 'width=600,height=700');
  };

  const handleSheetUrlChange = (url) => {
    const sheetId = parseGoogleSheetUrl(url);
    setFormData({ ...formData, sheetUrl: url, sheetId });
  };

  const handlePrefilledUrlChange = (url) => {
    const { baseUrl, entries } = parsePrefilledFormUrl(url);
    const { vendorIdEntryId, tokenEntryId, vendorNameEntryId } = identifyEntryIds(entries);
    setFormData({
      ...formData,
      prefilledUrl: url,
      formUrl: baseUrl || formData.formUrl,
      vendorIdEntryId: vendorIdEntryId || formData.vendorIdEntryId,
      tokenEntryId: tokenEntryId || formData.tokenEntryId,
      vendorNameEntryId: vendorNameEntryId || formData.vendorNameEntryId,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('請輸入問卷名稱');
      return;
    }

    const submitData = {
      name: formData.name,
      description: formData.description,
      google_sheet_id: formData.sheetId,
      sheet_name: formData.sheetName,
      google_form_url: formData.formUrl,
      google_form_prefilled_url: formData.prefilledUrl,
      vendor_id_entry_id: formData.vendorIdEntryId,
      token_entry_id: formData.tokenEntryId,
      vendor_name_entry_id: formData.vendorNameEntryId,
    };

    onSubmit(submitData);
  };

  // 產生測試預填連結
  const getTestPrefillUrl = () => {
    if (!formData.formUrl || !formData.vendorIdEntryId) return '';
    let url = `${formData.formUrl}?entry.${formData.vendorIdEntryId}=TEST_VENDOR`;
    if (formData.tokenEntryId) {
      url += `&entry.${formData.tokenEntryId}=TEST_TOKEN`;
    }
    if (formData.vendorNameEntryId) {
      url += `&entry.${formData.vendorNameEntryId}=TEST_NAME`;
    }
    return url;
  };

  const tabs = [
    { id: 'basic', label: '基本資訊' },
    { id: 'test', label: '連結測試' },
    { id: 'advanced', label: '進階設定' },
  ];

  // 新增模式只顯示基本資訊 tab
  const visibleTabs = mode === 'add' ? [tabs[0]] : tabs;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {mode === 'add' ? '新增問卷' : '問卷設定'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-gray-200">
          <div className="flex gap-1">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Tab 1: 基本資訊 */}
          {activeTab === 'basic' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* 問卷名稱 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  問卷名稱 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="例如：ESG 永續發展問卷"
                />
              </div>

              {/* 問卷描述 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">問卷說明</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="問卷用途說明..."
                />
              </div>

              {/* Google Sheet URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Google Sheet 網址 <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={formData.sheetUrl}
                  onChange={(e) => handleSheetUrlChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="https://docs.google.com/spreadsheets/d/.../edit"
                />
                {formData.sheetId && (
                  <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded text-sm">
                    <span className="text-green-800">✅ Sheet ID: </span>
                    <code className="bg-green-100 px-1 rounded font-mono text-xs">{formData.sheetId}</code>
                  </div>
                )}
              </div>

              {/* Sheet 名稱 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sheet 名稱</label>
                <input
                  type="text"
                  value={formData.sheetName}
                  onChange={(e) => setFormData({ ...formData, sheetName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="預設：表單回覆 1"
                />
              </div>

              {/* Google Form 預填連結 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Google Form 預填連結
                </label>
                <input
                  type="url"
                  value={formData.prefilledUrl}
                  onChange={(e) => handlePrefilledUrlChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="貼上預填連結"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Google Form → ⋮ → 取得預先填入的連結（供應商ID、驗證碼、供應商名稱 欄位都填任意值後取得）。貼上後系統會自動帶入下方三個 Entry ID，可手動修正。
                </p>

                {/* 三個明確可編輯的 Entry ID 欄位（不靠猜，最終以此為準） */}
                <div className="mt-3 grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      供應商ID Entry ID
                    </label>
                    <input
                      type="text"
                      value={formData.vendorIdEntryId}
                      onChange={(e) => setFormData({ ...formData, vendorIdEntryId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="例如 123456789"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      驗證碼 Entry ID
                    </label>
                    <input
                      type="text"
                      value={formData.tokenEntryId}
                      onChange={(e) => setFormData({ ...formData, tokenEntryId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="例如 987654321"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      供應商名稱 Entry ID <span className="text-gray-400 font-normal">（選填）</span>
                    </label>
                    <input
                      type="text"
                      value={formData.vendorNameEntryId}
                      onChange={(e) => setFormData({ ...formData, vendorNameEntryId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="若 Google Form 有「供應商名稱」題目才需填"
                    />
                  </div>
                </div>
              </div>

              {/* 提示 */}
              {mode === 'add' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    💡 建立問卷後，系統會自動部署 Apps Script 到您的 Google Sheet，讓問卷提交時可以自動同步資料到系統。
                  </p>
                </div>
              )}

              {/* 按鈕 */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {isLoading ? '處理中...' : '💾 儲存'}
                </button>
              </div>
            </form>
          )}

          {/* Tab 2: 連結測試 */}
          {activeTab === 'test' && (
            <div className="space-y-6">
              {/* Google Sheet */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <span className="text-green-600">📊</span> Google Sheet
                </h4>
                {formData.sheetId ? (
                  <div className="space-y-3">
                    <div className="text-sm">
                      <span className="text-gray-500">Sheet ID: </span>
                      <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-xs">{formData.sheetId}</code>
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={`https://docs.google.com/spreadsheets/d/${formData.sheetId}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition"
                      >
                        📂 開啟 Sheet
                      </a>
                      <button
                        type="button"
                        onClick={onSync}
                        disabled={isSyncing}
                        className="px-4 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition disabled:opacity-50"
                      >
                        {isSyncing ? '同步中...' : '🔄 手動同步'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">尚未設定 Google Sheet</p>
                )}
              </div>

              {/* Google Form */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <span className="text-purple-600">📝</span> Google Form
                </h4>
                {formData.formUrl ? (
                  <div className="space-y-3">
                    <div className="text-sm space-y-1">
                      <p>
                        <span className="text-gray-500">供應商ID Entry ID: </span>
                        <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-xs">
                          {formData.vendorIdEntryId || '未設定'}
                        </code>
                      </p>
                      <p>
                        <span className="text-gray-500">驗證碼 Entry ID: </span>
                        <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-xs">
                          {formData.tokenEntryId || '未設定'}
                        </code>
                      </p>
                      <p>
                        <span className="text-gray-500">供應商名稱 Entry ID: </span>
                        <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-xs">
                          {formData.vendorNameEntryId || '未設定'}
                        </code>
                      </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <a
                        href={formData.formUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 text-sm bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition"
                      >
                        📋 開啟表單（無預填）
                      </a>
                      {getTestPrefillUrl() && (
                        <a
                          href={getTestPrefillUrl()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 text-sm bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition"
                        >
                          🧪 開啟表單（有預填）
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">尚未設定 Google Form 連結</p>
                )}
              </div>

              {/* 關閉按鈕 */}
              <div className="flex justify-end pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  關閉
                </button>
              </div>
            </div>
          )}

          {/* Tab 3: 進階設定 */}
          {activeTab === 'advanced' && (
            <div className="space-y-6">
              {/* Apps Script 授權狀態 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <span>🔐</span> Apps Script 授權狀態
                </h4>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {authStatus.loading ? (
                      <span className="text-sm text-gray-500">檢查中...</span>
                    ) : authStatus.authorized ? (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 rounded-lg">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        <span className="text-sm text-green-700 font-medium">已授權</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 rounded-lg">
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        <span className="text-sm text-red-700 font-medium">未授權</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={checkAuthStatus}
                      className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                    >
                      🔄 重新檢查
                    </button>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={handleReauthorize}
                        className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                      >
                        🔑 重新授權
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Apps Script 部署狀態 */}
              {initialData?.apps_script?.script_id ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-green-800 flex items-center gap-2">
                      <span className="w-2.5 h-2.5 bg-green-500 rounded-full"></span>
                      Apps Script 已部署
                    </h4>
                    <div className="flex gap-2">
                      {initialData.apps_script.setup_url && (
                        <a
                          href={initialData.apps_script.setup_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 text-sm text-green-700 hover:bg-green-100 rounded-lg transition"
                        >
                          開啟 Script
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={onRedeploy}
                        disabled={isRedeploying}
                        className="px-3 py-1.5 text-sm text-gray-600 hover:bg-green-100 rounded-lg transition disabled:opacity-50"
                      >
                        {isRedeploying ? '部署中...' : '重新部署'}
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-green-700">
                    表單回應會自動同步到系統。如果同步功能異常，可嘗試重新部署。
                  </p>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-800 mb-2 flex items-center gap-2">
                    <span className="w-2.5 h-2.5 bg-yellow-500 rounded-full"></span>
                    Apps Script 未部署
                  </h4>
                  <p className="text-sm text-yellow-700 mb-3">
                    部署 Apps Script 後，表單回應會自動同步到系統。
                  </p>
                  <button
                    type="button"
                    onClick={onRedeploy}
                    disabled={isRedeploying || !formData.sheetId}
                    className="px-4 py-2 text-sm bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition disabled:opacity-50"
                  >
                    {isRedeploying ? '部署中...' : '🚀 部署 Apps Script'}
                  </button>
                  {!formData.sheetId && (
                    <p className="text-xs text-yellow-600 mt-2">請先設定 Google Sheet</p>
                  )}
                </div>
              )}

              {/* 危險區域 */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                  <span>⚠️</span> 危險區域
                </h4>
                <p className="text-sm text-red-700 mb-3">
                  刪除問卷將會移除所有相關的供應商指派和回覆資料，此操作無法復原。
                </p>
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                >
                  {isDeleting ? '刪除中...' : '🗑️ 刪除此問卷'}
                </button>
              </div>

              {/* 關閉按鈕 */}
              <div className="flex justify-end pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  關閉
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
